import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { api, errCode } from "../lib/api";
import { isNetErr, findLabel, getLocalBins } from "../lib/offline";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { QrScanner } from "../components/QrScanner";

export const useResolve = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [err, setErr] = useState("");
  const resolve = useCallback(async (code) => {
    setErr("");
    try {
      const { data } = await api.get(`/scan/${encodeURIComponent(code)}`);
      if (data.state === "active") return nav(`/bin/${data.bin_id}`, { replace: true });
      if (data.state === "void") return setErr("label_void");
      if (["admin", "farm_manager", "supervisor"].includes(user.role)) return nav(`/activate/${data.label.number}`, { replace: true });
      setErr("label_not_issued");
    } catch (e) {
      if (!isNetErr(e)) return setErr(errCode(e));
      const lb = await findLabel(code);
      if (!lb) return setErr("offline_label_unknown");
      const local = Object.values(await getLocalBins()).find((b) => b.label_id === lb.id);
      const binId = local?.id || lb.bin_id;
      if (binId) return nav(`/bin/${binId}`, { replace: true });
      nav(`/activate/${lb.number}`, { replace: true });
    }
  }, [nav, user]);
  return { resolve, err };
};

export const ScanError = ({ err }) => {
  const { t, te } = useLang();
  if (!err) return null;
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border-2 border-red-300 text-red-900 font-semibold" data-testid="scan-error">
      <ShieldAlert size={24} /> {err === "label_void" ? t("label_void") : te(err)}
    </div>
  );
};

export default function Scan() {
  const { t } = useLang();
  const [sp] = useSearchParams();
  const { resolve, err } = useResolve();
  const [code, setCode] = useState("");
  const [key, setKey] = useState(0);
  const onCode = useCallback((c) => { resolve(c); setKey((k) => k + 1); }, [resolve]);

  return (
    <div className="max-w-md mx-auto space-y-5" data-testid="scan-page">
      <h1 className="text-2xl font-extrabold">{sp.get("mode") === "dock" ? t("dock") : sp.get("mode") === "ph" ? t("ph_scan") : t("scan_label")}</h1>
      <p className="text-slate-600 text-sm">{sp.get("mode") === "dock" ? t("dock_scan_hint") : sp.get("mode") === "ph" ? t("ph_scan_hint") : t("scan_hint")}</p>
      <QrScanner key={key} onCode={onCode} />
      <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) resolve(code.trim()); }} className="space-y-2">
        <span className="eyebrow">{t("manual_entry")}</span>
        <div className="flex gap-2">
          <input className="field font-mono uppercase" placeholder="CH-2026-000001" value={code} onChange={(e) => setCode(e.target.value)} data-testid="manual-label-input" />
          <button className="btn-primary" data-testid="manual-label-submit">{t("open")}</button>
        </div>
      </form>
      <ScanError err={err} />
    </div>
  );
}
